import {
  Button,
  ButtonGroup,
  CopyableLink,
  DrawerCard,
  DrawerField,
  DrawerTitle,
  Editor,
  Html,
  Spinner,
  Toggle,
} from "@mixitone/components";
import LinkIcon from "@mixitone/icons/link.svg";
import TrophyIcon from "@mixitone/icons/trophy.svg";
import { ApplicationView } from "@mixitone/mvc";
import { entries } from "@mixitone/util";
import { CompetitionDrawerController } from "components/Drawers/Competition/CompetitionDrawerController";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { ValueComponent } from "components/Forms/FormValueComponents";
import {
  FormatSettingsFields,
  TournamentCompetitionFormController,
} from "components/Forms/TournamentCompetitionForm";
import TournamentStandings from "components/Tournament/TournamentStandings/TournamentStandings";
import { ScopedPrintGroupsButton } from "components/Tournament/TournamentStandings/PrintGroupsButton";
import { ScopedLockGroupsButton } from "components/Tournament/TournamentStandings/LockGroupsButton";
import {
  roundRobinGroupsLocked,
  roundRobinPlayoffSettings,
} from "components/Pages/Tournament/tournamentHelpers";
import { RoutedLink } from "components/common/Link";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import { injectModal } from "lib/injectParams";
import { useEffect, useMemo } from "react";
import type { FormatName } from "tournament-formats";

const ControlledTournamentCompetitionForm: React.FC = () => {
  const controller = TournamentCompetitionFormController.use();
  const { loading, saving, record, formatSchema, formatSettings } = controller.state;
  const fields = useMemo(() => controller.fields(), []);

  // Watch format changes and trigger format action
  useEffect(() => {
    if (record?.format) {
      controller.actionChangeFormat(record.format as FormatName);
    }
  }, [record?.format]);

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <Spinner size={64} />
      </div>
    );
  }

  const showFormatSettings =
    formatSchema && entries(formatSchema).filter(([, schema]) => !schema.hidden).length > 0;

  return (
    <Form name="competition">
      <fieldset disabled={saving} className="space-y-6">
        <DrawerCard>
          {[fields.name, fields.entry_type, fields.min_entries, fields.max_entries].map((field, i) => {
            // Adding select variant for entry_type
            if (field.name === "entry_type" && field.type === "select") {
              return (
                <DrawerField label={field.label} key={i}>
                  <ValueComponent field={field} record={record} autoFocus={i === 0} variant="select" />
                </DrawerField>
              );
            }

            return (
              <DrawerField label={field.label} key={i}>
                <ValueComponent field={field} record={record} autoFocus={i === 0} />
              </DrawerField>
            );
          })}
        </DrawerCard>
        <DrawerCard>
          <div className="">
            <h3 className="mb-4 text-lg font-medium">Format</h3>
            <ValueComponent field={fields.format} record={record} />
          </div>
          {showFormatSettings && (
            <div className="pt-4 mt-4 border-t">
              <h3 className="mb-4 text-lg font-medium">Format settings</h3>
              <FormatSettingsFields
                schema={formatSchema}
                settings={formatSettings}
                onChange={(newSettings) => {
                  controller.state.formatSettings = newSettings;
                }}
              />
            </div>
          )}
        </DrawerCard>

        <FormButtons />
      </fieldset>
    </Form>
  );
};

const TournamentCompetitionForm = TournamentCompetitionFormController.scope(
  ApplicationView(ControlledTournamentCompetitionForm),
);

const CompetitionDrawerContent = ApplicationView(() => {
  const controller = CompetitionDrawerController.use();
  const { competition, loading, editingDescription } = controller.state;

  if (loading) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <DrawerCard>
          <DrawerField label="Name">&nbsp;</DrawerField>
          <hr />
          <DrawerField label="Registered players">&nbsp;</DrawerField>
          <hr />
          <DrawerField label="Entries">&nbsp;</DrawerField>
        </DrawerCard>
      </div>
    );
  }

  const { registeredPlayerCount, entryCount } = controller;
  const playoffSettings = roundRobinPlayoffSettings(competition);

  return (
    <div className="flex flex-col gap-4">
      <DrawerCard>
        <DrawerField label="Name">
          <div className="flex items-center justify-between w-full gap-2">
            <span>{competition.name}</span>
            <Button kind="action" onClick={() => controller.actionEditCompetition(true)}>
              Edit
            </Button>
          </div>
        </DrawerField>
        <hr />
        <DrawerField label="Registered players">
          <div className="flex items-center justify-between w-full gap-2">
            <span>{registeredPlayerCount}</span>
            {competition.tournament_session_id && (
              <RoutedLink
                kind="plain"
                to="/tournament/:id/people/players"
                state={{ tournament_competition_id: competition.id }}
                params={{ id: competition.tournament_session_id }}
              >
                <LinkIcon className="w-[20px] stroke-gray-500" />
                View players
              </RoutedLink>
            )}
          </div>
        </DrawerField>
        <hr />
        <DrawerField label="Entries">
          <div className="flex items-center justify-between w-full gap-2">
            <span>{entryCount}</span>
            {competition.tournament_session_id && (
              <RoutedLink
                kind="plain"
                to="/tournament/:id/people/entries"
                state={{ tournament_competition_id: competition.id }}
                params={{ id: competition.tournament_session_id }}
              >
                <LinkIcon className="w-[20px] stroke-gray-500" />
                View entries
              </RoutedLink>
            )}
          </div>
        </DrawerField>
      </DrawerCard>

      <DrawerCard>
        <h2 className="text-lg font-medium">Sign up</h2>

        <DrawerField label="Enabled">
          <Toggle
            checked={competition.status === "entry_open"}
            disabled={!competition.canEnableSignUp}
            onChange={(e) => controller.actionEnableSignUp(e.target.checked)}
          />
        </DrawerField>

        <DrawerField label="Sign up link">
          <CopyableLink url={competition.registrationLink(import.meta.env.VITE_PORTAL_URL)} />
        </DrawerField>

        {editingDescription && (
          <div className="flex flex-col gap-1">
            <Editor
              value={competition.description || ""}
              onChange={(event) => {
                competition.description = event.target.value;
              }}
            />
            <div className="flex justify-end">
              <ButtonGroup>
                <Button className="text-xs" onClick={() => controller.actionSaveDescription()}>
                  Save
                </Button>
                <Button
                  className="text-xs"
                  kind="secondary"
                  onClick={() => controller.actionCancelDescription()}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )}
        {!editingDescription && (
          <DrawerField label="Description" className="items-start">
            <div>
              <Html html={competition.description || ""} />
              <div className="flex justify-end">
                <Button kind="action" onClick={() => controller.actionEditDescription(true)}>
                  Edit
                </Button>
              </div>
            </div>
          </DrawerField>
        )}
      </DrawerCard>

      {/* Competition Standings */}
      {competition.id && (
        <DrawerCard title="Competition Standings">
          {competition.format === "RoundRobin" && (
            <div className="flex flex-col items-end gap-2 mb-4">
              <div className="flex justify-end gap-2">
                <ScopedLockGroupsButton competition={competition} competitionId={competition.id!} />
                <Button
                  kind="secondary"
                  onClick={() => controller.actionRegenerateGroups()}
                  disabled={controller.state.loading || roundRobinGroupsLocked(competition)}
                  title={
                    roundRobinGroupsLocked(competition) ? "Unlock groups before regenerating them" : undefined
                  }
                >
                  Regenerate groups
                </Button>
                {playoffSettings.enablePlayoffs && playoffSettings.divisions > 1 && (
                  <Button
                    kind="secondary"
                    onClick={() => controller.actionResetPlayoffDivisions()}
                    disabled={controller.state.loading}
                  >
                    Reset playoff divisions
                  </Button>
                )}
                <ScopedPrintGroupsButton competition={competition} competitionId={competition.id!} />
              </div>
              {roundRobinGroupsLocked(competition) && (
                <p className="text-xs text-gray-600">
                  Groups are locked. New entries stay unassigned until you place them.
                </p>
              )}
            </div>
          )}
          <TournamentStandings competitionId={competition.id} competition={competition} />
        </DrawerCard>
      )}
    </div>
  );
});

const CompetitionDrawer = ({ id, competitionId }: { id: string; competitionId: string }) => {
  const controller = CompetitionDrawerController.use();
  const { editing, loading } = controller.state;

  return (
    <RoutedDrawer spinner={loading}>
      <DrawerTitle
        icon={<TrophyIcon className="mr-2 w-[24px] fill-gray-500" />}
        contextLink={<DrawerContextLink context="tournament" params={{ id }} />}
      >
        Competition
      </DrawerTitle>
      {!editing && <CompetitionDrawerContent />}
      {editing && (
        <TournamentCompetitionForm
          onSubmit={async () => controller.actionEditCompetition(false)}
          attributes={{ tournament_session_id: id }}
          id={competitionId}
        />
      )}
    </RoutedDrawer>
  );
};

export default injectModal(CompetitionDrawerController.scope(ApplicationView(CompetitionDrawer)));
